import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocale } from '../../../locales';
import type { CanvasSettings } from '../../../editor/types';
import { clampNumber, cloneCanvasSettings } from '../lib/canvas';

interface SettingsModalProps {
  canvas: CanvasSettings;
  defaults: CanvasSettings;
  onClose: () => void;
  onSave: (settings: CanvasSettings) => void;
  onReset: (settings: CanvasSettings) => void;
  onAlignCanvas?: (alignment: 'left' | 'center' | 'right') => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ canvas, defaults, onClose, onSave, onReset, onAlignCanvas }) => {
  const { t } = useLocale();
  const contentRef = useRef<HTMLDivElement>(null);
  const [formState, setFormState] = useState(() => ({
    background: canvas.background,
    paddingX: String(canvas.padding?.x || 32),
    paddingY: String(canvas.padding?.y || 32),
    innerPaddingTop: String(canvas.innerPadding?.top || 0),
    innerPaddingRight: String(canvas.innerPadding?.right || 0),
    innerPaddingBottom: String(canvas.innerPadding?.bottom || 0),
    innerPaddingLeft: String(canvas.innerPadding?.left || 0),
    gridEnabled: canvas.grid.enabled,
    gridSize: String(canvas.grid.size),
  }));


  useEffect(() => {
    contentRef.current?.focus();
  }, []);

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [onClose]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const next: CanvasSettings = {
        width: canvas.width,
        height: canvas.height,
        lockWidth: canvas.lockWidth,
        background: formState.background,
        expandable: true,
        padding: {
          x: clampNumber(formState.paddingX, defaults.padding.x, 0, 500),
          y: clampNumber(formState.paddingY, defaults.padding.y, 0, 500),
        },
        innerPadding: {
          top: clampNumber(formState.innerPaddingTop, 0, 0, 200),
          right: clampNumber(formState.innerPaddingRight, 0, 0, 200),
          bottom: clampNumber(formState.innerPaddingBottom, 0, 0, 200),
          left: clampNumber(formState.innerPaddingLeft, 0, 0, 200),
        },
        grid: {
          enabled: formState.gridEnabled,
          size: clampNumber(formState.gridSize, canvas.grid.size, 4, 240),
        },
        hiddenArea: canvas.hiddenArea,
        question: canvas.question,
      };
      onSave(cloneCanvasSettings(next));
    },
    [canvas.grid.size, canvas.height, canvas.hiddenArea, canvas.question, defaults.padding.x, defaults.padding.y, formState, onSave],
  );

  const handleReset = useCallback(() => {
    const resetSettings = cloneCanvasSettings(defaults);
    setFormState({
      background: resetSettings.background,
      paddingX: String(resetSettings.padding.x),
      paddingY: String(resetSettings.padding.y),
      innerPaddingTop: String(resetSettings.innerPadding?.top || 0),
      innerPaddingRight: String(resetSettings.innerPadding?.right || 0),
      innerPaddingBottom: String(resetSettings.innerPadding?.bottom || 0),
      innerPaddingLeft: String(resetSettings.innerPadding?.left || 0),
      gridEnabled: resetSettings.grid.enabled,
      gridSize: String(resetSettings.grid.size),
    });
    onReset(resetSettings);
  }, [defaults, onReset]);

  const handleTransparentBackground = useCallback(() => {
    setFormState((prev) => ({ ...prev, background: 'transparent' }));
  }, []);

  return (
    <div className="qe-modal" role="presentation">
      <div className="qe-modal__backdrop" onClick={handleClose} role="button" aria-label="Kapat" />
      <div
        className="qe-modal__content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="qe-settings-modal-title"
        tabIndex={-1}
        ref={contentRef}
      >
        <header className="qe-modal__header">
          <div>
            <h2 id="qe-settings-modal-title">{t("settings.title")}</h2>
            <p className="qe-modal__subtitle">{t("settings.subtitle")}</p>
          </div>
          <button className="qe-button qe-button--ghost" type="button" onClick={handleClose}>
            {t("settings.close")}
          </button>
        </header>
        <form className="qe-modal__body" onSubmit={handleSubmit}>
          <section className="qe-modal__section">
            <h3>{t("settings.canvasOuterPadding")}</h3>
            <div className="qe-modal__grid">
              <div className="qe-modal__field">
                <label className="qe-form__label" htmlFor="canvas-padding-x-input">
                  {t("settings.leftPx")}
                </label>
                <input
                  id="canvas-padding-x-input"
                  className="qe-input"
                  type="number"
                  min={0}
                  max={500}
                  value={formState.paddingX}
                  onChange={(event) =>
                    setFormState((prev) => ({ ...prev, paddingX: event.target.value }))
                  }
                />
              </div>
              <div className="qe-modal__field">
                <label className="qe-form__label" htmlFor="canvas-padding-y-input">
                  {t("settings.topPx")}
                </label>
                <input
                  id="canvas-padding-y-input"
                  className="qe-input"
                  type="number"
                  min={0}
                  max={500}
                  value={formState.paddingY}
                  onChange={(event) =>
                    setFormState((prev) => ({ ...prev, paddingY: event.target.value }))
                  }
                />
              </div>
            </div>
          </section>

          <section className="qe-modal__section">
            <h3>{t("settings.canvasInnerPadding")}</h3>
            <div className="qe-modal__grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
              <div className="qe-modal__field">
                <label className="qe-form__label" htmlFor="canvas-inner-padding-top">
                  {t("settings.topPx")}
                </label>
                <input
                  id="canvas-inner-padding-top"
                  className="qe-input"
                  type="number"
                  min={0}
                  max={200}
                  value={formState.innerPaddingTop}
                  onChange={(event) =>
                    setFormState((prev) => ({ ...prev, innerPaddingTop: event.target.value }))
                  }
                />
              </div>
              <div className="qe-modal__field">
                <label className="qe-form__label" htmlFor="canvas-inner-padding-right">
                  {t("settings.rightPx")}
                </label>
                <input
                  id="canvas-inner-padding-right"
                  className="qe-input"
                  type="number"
                  min={0}
                  max={200}
                  value={formState.innerPaddingRight}
                  onChange={(event) =>
                    setFormState((prev) => ({ ...prev, innerPaddingRight: event.target.value }))
                  }
                />
              </div>
              <div className="qe-modal__field">
                <label className="qe-form__label" htmlFor="canvas-inner-padding-bottom">
                  {t("settings.bottomPx")}
                </label>
                <input
                  id="canvas-inner-padding-bottom"
                  className="qe-input"
                  type="number"
                  min={0}
                  max={200}
                  value={formState.innerPaddingBottom}
                  onChange={(event) =>
                    setFormState((prev) => ({ ...prev, innerPaddingBottom: event.target.value }))
                  }
                />
              </div>
              <div className="qe-modal__field">
                <label className="qe-form__label" htmlFor="canvas-inner-padding-left">
                  {t("settings.leftPx")}
                </label>
                <input
                  id="canvas-inner-padding-left"
                  className="qe-input"
                  type="number"
                  min={0}
                  max={200}
                  value={formState.innerPaddingLeft}
                  onChange={(event) =>
                    setFormState((prev) => ({ ...prev, innerPaddingLeft: event.target.value }))
                  }
                />
              </div>
            </div>
          </section>

          <section className="qe-modal__section">
            <h3>{t("settings.grid")}</h3>
            <div className="qe-modal__row">
              <label className="qe-toggle">
                <input
                  type="checkbox"
                  checked={formState.gridEnabled}
                  onChange={(event) =>
                    setFormState((prev) => ({ ...prev, gridEnabled: event.target.checked }))
                  }
                />
                <span>{t("settings.showGrid")}</span>
              </label>
              <div className="qe-modal__field">
                <label className="qe-form__label" htmlFor="grid-size-input">
                  {t("settings.cellSizePx")}
                </label>
                <input
                  id="grid-size-input"
                  className="qe-input"
                  type="number"
                  min={4}
                  max={240}
                  disabled={!formState.gridEnabled}
                  value={formState.gridSize}
                  onChange={(event) =>
                    setFormState((prev) => ({ ...prev, gridSize: event.target.value }))
                  }
                />
              </div>
            </div>
          </section>

          <section className="qe-modal__section">
            <h3>{t("settings.backgroundSection")}</h3>
            <div className="qe-modal__row qe-modal__row--spread">
              <label className="qe-inline-input">
                <span className="qe-inline-input__label">{t("settings.colorLabel")}</span>
                <input
                  className="qe-input qe-input--color"
                  type="color"
                  value={formState.background === 'transparent' ? '#ffffff' : formState.background}
                  onChange={(event) =>
                    setFormState((prev) => ({ ...prev, background: event.target.value }))
                  }
                />
              </label>
              <button
                className="qe-button qe-button--ghost"
                type="button"
                onClick={handleTransparentBackground}
              >
                {t("settings.transparent")}
              </button>
            </div>
          </section>

          <footer className="qe-modal__footer">
            <button className="qe-button qe-button--ghost" type="button" onClick={handleReset}>
              {t("settings.resetToDefaults")}
            </button>
            <div className="qe-modal__actions">
              <button className="qe-button qe-button--ghost" type="button" onClick={handleClose}>
                {t("settings.cancel")}
              </button>
              <button className="qe-button qe-button--primary" type="submit">
                {t("settings.save")}
              </button>
            </div>
          </footer>
        </form>
      </div>
    </div>
  );
};

export default SettingsModal;
